import type { PostHog } from "posthog-js";

import { buildInitOptions } from "./config";

/**
 * pending caches the in-flight (or settled) SDK load so repeated init calls are
 * idempotent, mirroring the session-bootstrap cache the web app's router guard
 * uses. client holds the initialized SDK once it resolves, so the capture helpers
 * stay synchronous and no call site has to await anything.
 */
let pending: Promise<PostHog | null> | null = null;
let client: PostHog | null = null;

/**
 * AnalyticsConfig is the resolved per-property configuration: the project token,
 * the ingest and application origins, and whether this surface records sessions.
 */
export interface AnalyticsConfig {
  apiHost: string;
  replay: boolean;
  token: string;
  uiHost: string;
}

/**
 * initAnalytics loads posthog-js and initializes it. The import is dynamic so the
 * SDK lands in its own chunk instead of the entry bundle, and the fetch is
 * asynchronous and browser-prioritized. That is what keeps analytics off the
 * critical path, so callers invoke this at bootstrap rather than deferring it:
 * session replay records only from the moment the recorder starts, and a delayed
 * start silently discards the opening of the session.
 *
 * A failed load resolves to null rather than rejecting: analytics is not allowed
 * to surface as an application error, and every capture helper already no-ops
 * without a client. The failure is not retried — a token that cannot load once
 * will not load on the next call either, and a retry loop would be worse than
 * missing data.
 */
export const initAnalytics = ({
  apiHost,
  replay,
  token,
  uiHost,
}: AnalyticsConfig): Promise<PostHog | null> => {
  if (!pending) {
    pending = import("posthog-js")
      .then((module) => {
        const posthog = module.default;
        posthog.init(token, buildInitOptions({ apiHost, replay, uiHost }));
        client = posthog;
        exposeForDebugging(posthog);

        return posthog;
      })
      .catch(() => null);
  }

  return pending;
};

/**
 * analyticsClient returns the initialized SDK, or null when it has not loaded
 * (or was never enabled for this surface). Every capture helper reads it through
 * here so "analytics is off" is one branch in one place.
 */
export const analyticsClient = (): PostHog | null => client;

/**
 * exposeForDebugging publishes the initialized SDK as `window.posthog`, which is
 * where PostHog's own documentation, its support team, and every console recipe
 * expect to find it. Importing the module rather than installing the HTML snippet
 * means nothing sets that global for us, which makes the whole integration
 * opaque from a browser console: `posthog.sessionRecording.status`, the single
 * question worth asking when a replay does not appear, is simply unanswerable.
 *
 * There is nothing to protect here. The project token is public by design and
 * already sits in the bundle, and every snippet-based install exposes exactly
 * this object, so the only thing withholding it buys is a harder diagnosis.
 */
const exposeForDebugging = (posthog: PostHog): void => {
  if (typeof window === "undefined") {
    return;
  }

  (window as typeof window & { posthog?: PostHog }).posthog = posthog;
};
